import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react"

import { useAsyncEffect } from "@app/scripts/hooks/use-async-effect"

import {
  CartRecommendationsResponse,
  SearchspringAutocomplete,
  SearchspringCategory,
  SearchspringCollection,
  SearchspringFetcher,
  SearchspringProduct,
  SearchspringRecommend,
  SearchspringSearch,
  SearchspringSearchResponse,
  SearchspringSuggest,
  SearchspringSuggestResponse,
  SearchspringTrending,
  SearchspringTrendingResponse,
} from "@app/scripts/lib/searchspring"

import { ImpressionModule } from "@app/scripts/lib/beacon-impression"
import { BeaconResultsProvider } from "@app/scripts/providers/beacon-results"

type UseSearchspringInput<
  Client extends SearchspringFetcher<Response>,
  Response,
> = {
  client: Client
  options?: Options
  isInitiallyRun?: boolean
  shouldSkipEmptyOptions?: boolean
}

export function useSearchspring<
  Client extends SearchspringFetcher<Response>,
  Response,
>({
  client,
  options: _options = new Map(),
  isInitiallyRun = true,
  shouldSkipEmptyOptions = false,
}: UseSearchspringInput<Client, Response>) {
  // Request State
  const [response, setResponse] = useState<Response>()

  // Object for managing filtering
  const params = useRef(client.params)
  const [options, setOptions] = useState(new Map(_options))

  // Update the params reference whenever options change
  // A function for correctly updating the filtering array for Searchspring
  const refine = useCallback(
    (key: string, value: OptionValue) => {
      if (value === options.get(key)) return
      setOptions((prev) => {
        const next = new Map(prev)
        if (!value) next.delete(key)
        else next.set(key, value)
        return next
      })
    },
    [setOptions]
  )

  const [isLoading, error] = useAsyncEffect(
    async (isInitialRun) => {
      // Check here to see if it's passing on load
      if (!isInitiallyRun && isInitialRun) return
      if (shouldSkipEmptyOptions && options.size === 0) return
      const params = Object.fromEntries(options.entries())
      client.updateParams(params)
      const response = await client.fetch()
      setResponse(response)
    },

    async () => {
      client.controller.abort(
        "Outdated Searchspring payload, aborting current request"
      )
      client.refreshAbortController()
    },
    [options]
  )

  return {
    isLoading,
    response,
    error,
    options,
    params,
    setOptions,
    refine,
  }
}

const SearchspringContext = createContext(
  {} as ReturnType<typeof useSearchspring>
)

export const useSearchspringContext = <
  Client extends SearchspringFetcher<Response>,
  Response,
>() =>
  useContext(SearchspringContext) as ReturnType<
    typeof useSearchspring<Client, Response>
  >

export const useSearchspringSearchContext = () =>
  useSearchspringContext<
    SearchspringFetcher<SearchspringSearchResponse>,
    SearchspringSearchResponse
  >()

export const useSearchspringTrendingContext = () =>
  useSearchspringContext<
    SearchspringFetcher<SearchspringTrendingResponse>,
    SearchspringTrendingResponse
  >()

export const useSearchspringRecommendContext = () =>
  useSearchspringContext<
    SearchspringFetcher<CartRecommendationsResponse[]>,
    CartRecommendationsResponse[]
  >()

export const useSearchspringSuggestContext = () =>
  useSearchspringContext<
    SearchspringFetcher<SearchspringSuggestResponse[]>,
    SearchspringSuggestResponse[]
  >()

export type OptionValue = string | Set<string> | number
export type Options = Map<string, OptionValue>

type ProviderCoreProps<
  Client extends SearchspringFetcher<Response>,
  Response,
> = {
  client: Client
  children: ReactNode
  options: Options
  isInitiallyRun?: boolean
}

export function SearchspringProvider<
  Client extends SearchspringFetcher<Response>,
  Response,
>({
  client,
  options,
  children,
  isInitiallyRun = true,
}: ProviderCoreProps<Client, Response>) {
  const value = useSearchspring({ client, options, isInitiallyRun })
  return (
    <SearchspringContext.Provider value={value}>
      {children}
    </SearchspringContext.Provider>
  )
}

type ProviderGeneratorProps = {
  children: ReactNode
  category: SearchspringCategory
  isInitiallyRun?: boolean
  /**
   * Override the Beacon 2.0 module used for tracking. Useful when the same
   * fetcher (e.g. SearchspringSearch) drives a different surface like the
   * autocomplete drawer that should report under the `autocomplete` module.
   */
  beaconModule?: ImpressionModule
  [option: string]: OptionValue | ReactNode
}

type ResponseWithId = {
  responseId?: string
  resultId?: string
  tid?: string
}

function extractResponseId(response: unknown): string | null {
  if (!response) return null
  // Recommendations return an array of profile responses; the docs say
  // batched profiles share one responseId - take it from the first profile.
  if (Array.isArray(response)) {
    if (response.length === 0) return null
    return extractResponseId(response[0])
  }
  const r = response as ResponseWithId & Record<string, unknown>
  if (typeof r.responseId === "string" && r.responseId) return r.responseId
  if (typeof r.resultId === "string" && r.resultId) return r.resultId
  if (typeof r.tid === "string" && r.tid) return r.tid
  return null
}

function BeaconResultsSync({
  module,
  tag,
  children,
}: {
  module: ImpressionModule
  tag?: string
  children: ReactNode
}) {
  const { response } = useSearchspringContext()
  const responseId = extractResponseId(response)
  return (
    <BeaconResultsProvider module={module} responseId={responseId} tag={tag}>
      {children}
    </BeaconResultsProvider>
  )
}

export function generateSearchspringProvider<
  Client extends typeof SearchspringFetcher<Response>,
  Response,
>(ClientClass: Client, defaultBeaconModule?: ImpressionModule) {
  return function ({
    children,
    category,
    isInitiallyRun,
    beaconModule,
    ...options
  }: ProviderGeneratorProps) {
    const client = useRef(new ClientClass(category))
    const moduleName = beaconModule ?? defaultBeaconModule
    const wrapped = moduleName ? (
      <BeaconResultsSync
        module={moduleName}
        tag={typeof options.profile === "string" ? options.profile : undefined}
      >
        {children}
      </BeaconResultsSync>
    ) : (
      children
    )
    return (
      <SearchspringProvider
        client={client.current}
        options={new Map(Object.entries(options))}
        isInitiallyRun={isInitiallyRun}
      >
        {wrapped}
      </SearchspringProvider>
    )
  }
}

// All definitions for usable Searchspring providers are below:

export const SearchspringAutocompleteProvider = generateSearchspringProvider(
  SearchspringAutocomplete,
  "autocomplete"
)
export const SearchspringProductsProvider =
  generateSearchspringProvider(SearchspringProduct)
export const SearchspringCollectionProvider = generateSearchspringProvider(
  SearchspringCollection,
  "category"
)
export const SearchspringRecommendProvider = generateSearchspringProvider(
  SearchspringRecommend,
  "recommendations"
)
export const SearchspringTrendingProvider =
  generateSearchspringProvider(SearchspringTrending)
export const SearchspringSearchProvider = generateSearchspringProvider(
  SearchspringSearch,
  "search"
)
export const SearchspringSuggestProvider =
  generateSearchspringProvider(SearchspringSuggest)
